/**
 * Everything the bench shows, from one world: the rays, the images, what
 * happened inside each block, and what is wrong.
 *
 * `selectedId` only changes WHICH rays are drawn — construction rays for the
 * selected lens or mirror — never any number.
 */
import { constructionAims, fanAims, surfacesOf, targetFor } from './construction';
import { diagnose } from './diagnose';
import { imageReport } from './image';
import { WHITE_LIGHT, isWhite, wavelengthOf } from './kinds/sources';
import { defOf } from './registry';
import { traceRay } from './trace';
import { ImageReport, Part, Problem, Ray, RayMode, Surface, Vec2, Vertex, World } from './types';
import { angleBetween, cross, sub, unit } from './vec';

/** A ray's passage through one block: in at one face, out at another. */
export interface Crossing {
    partId: string;
    rayId: string;
    entry: Vertex;
    exit: Vertex;
    /** Angle between the ray going in and the ray coming out, degrees. */
    deviation: number;
    /** When it comes out parallel to how it went in: how far it was shifted sideways, cm. */
    lateral?: number;
    /** Reflected totally at some face inside. */
    tir: boolean;
}

export interface Analysis {
    surfaces: Surface[];
    rays: Ray[];
    images: ImageReport[];
    crossings: Crossing[];
    problems: Problem[];
}

export interface AnalyseOptions { selectedId?: string }

export function raysOf(w: World, surfaces: Surface[], opts: AnalyseOptions = {}): Ray[] {
    const rays: Ray[] = [];
    const push = (part: Part, from: Vec2, dir: Vec2, point: string, role: Ray['role'], extra: Partial<Ray> = {}, unbounded?: Set<string>, nm = wavelengthOf(part, w)) => {
        const traced = traceRay(surfaces, from, dir, { ...(unbounded ? { unbounded } : {}), wavelength: nm });
        rays.push({ id: `${part.id}#${rays.length}`, source: { partId: part.id, point }, wavelength: nm, role, ...extra, ...traced });
    };

    for (const part of w.parts) {
        const kind = defOf(part);
        const tracedPoints = kind.points?.(part, w).filter(p => p.traced) ?? [];
        const anchor = tracedPoints[0];
        const el = anchor ? targetFor(w, surfaces, anchor.at, opts.selectedId) : null;

        if (tracedPoints.length && el) {
            const unbounded = new Set(surfaces.filter(s => s.partId === el.partId).map(s => s.id));
            for (const pt of tracedPoints) {
                const source = { partId: part.id, point: pt.id, ...(pt.t !== undefined ? { t: pt.t } : {}) };
                // Each source says how it sends its rays, and how many in a fan.
                const data = part.data as { rayMode?: RayMode; rays?: number };
                if ((data.rayMode ?? 'textbook') === 'textbook') {
                    for (const aim of constructionAims(pt.at, el)) {
                        push(part, pt.at, aim.direction, pt.id, 'construction', { construction: aim.rule, source }, unbounded);
                        // A construction ray that would meet the element outside its
                        // aperture is not drawn: bent where there is no glass, it
                        // teaches something false. Two rays still fix the image, and
                        // the image is found from probes, never from these.
                        if (rays[rays.length - 1].vertices.some(v => v.outsideAperture)) rays.pop();
                    }
                } else {
                    for (const d of fanAims(pt.at, el, Math.max(2, Math.round(data.rays ?? 7)))) push(part, pt.at, d, pt.id, 'fan', { source });
                }
            }
        } else if (kind.emit) {
            // White light is three rays, one per colour, that part only where
            // a dispersing material reads their wavelengths differently.
            const colours = isWhite(part) ? WHITE_LIGHT : [wavelengthOf(part, w)];
            const extra: Partial<Ray> = isWhite(part) ? { white: true } : {};
            for (const e of kind.emit(part, w)) {
                for (const nm of colours) push(part, e.from, e.direction, e.point, e.role, extra, undefined, nm);
            }
        }
    }
    return rays;
}

/** Consecutive vertices on one refracting part are one passage through it. */
export function crossingsOf(rays: Ray[], surfaces: Surface[]): Crossing[] {
    const refracting = new Set(surfaces.filter(s => s.rule.law === 'refract').map(s => s.partId));
    const out: Crossing[] = [];
    for (const ray of rays) {
        const vs = ray.vertices;
        for (let i = 1; i < vs.length; i++) {
            const partId = vs[i].partId;
            if (!partId || !refracting.has(partId)) continue;
            let j = i;
            while (j + 1 < vs.length && vs[j + 1].partId === partId) j++;
            if (j > i) {
                const into = vs[i - 1].direction;
                const outOf = vs[j].direction;
                const deviation = angleBetween(into, outOf);
                const crossing: Crossing = {
                    partId, rayId: ray.id, entry: vs[i], exit: vs[j], deviation,
                    tir: vs.slice(i, j + 1).some(v => v.tir),
                };
                if (deviation < 1e-6) crossing.lateral = Math.abs(cross(sub(vs[j].at, vs[i].at), unit(into)));
                out.push(crossing);
            }
            i = j;
        }
    }
    return out;
}

export function analyse(w: World, opts: AnalyseOptions = {}): Analysis {
    const surfaces = surfacesOf(w);
    const rays = raysOf(w, surfaces, opts);
    const images = w.parts
        .map(p => imageReport(w, p, surfaces, opts.selectedId))
        .filter((r): r is ImageReport => r !== null);
    const crossings = crossingsOf(rays, surfaces);
    return { surfaces, rays, images, crossings, problems: diagnose(w, rays, images) };
}
